import React from "react";
import { Link } from "react-router-dom";
import {
  FaInstagram,
  FaLinkedin,
  FaWhatsapp,
  FaYoutube,
} from "react-icons/fa";

import Logo from "../../assets/Logo/kodevedic-logo-a-learning-platform-digital-learning-lms-platform-full.png";
import { FooterLink2 } from "../../data/footer-links";
import { DS } from "../../utils/designSystem";

/* ── Data ──────────────────────────────────────────────────────── */
const PLATFORM_LINKS = [
  { label: "About",               to: "/about" },
  { label: "Careers",             to: "/careers" },
  { label: "Contact",             to: "/contact" },
  { label: "Affiliates",          to: "/affiliates" },
  { label: "Events",              to: "/events" },
  { label: "Verify Certificate",  to: "/verify-certificate" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Cookie Policy",  to: "/cookie-policy" },
  { label: "Terms",          to: "/terms" },
];

const SOCIALS = [
  { icon: FaInstagram, href: "https://www.instagram.com/joinspaf/",             label: "Instagram" },
  { icon: FaLinkedin,  href: "https://www.linkedin.com/company/joinspaf/",      label: "LinkedIn" },
  { icon: FaWhatsapp,  href: "https://wa.me/916362720677",                      label: "WhatsApp" },
  { icon: FaYoutube,   href: "https://www.youtube.com/@joinspaf",               label: "YouTube" },
];

/* ── Reusable column ───────────────────────────────────────────── */
function LinkColumn({ title, links }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold uppercase tracking-wider text-richblack-100">
        {title}
      </p>
      <ul className="flex flex-col gap-2">
        {links.map(({ label, title: t, to, link }) => (
          <li key={label ?? t}>
            <Link
              to={to ?? link}
              className="text-sm text-richblack-400 transition-colors duration-150 hover:text-richblack-5"
            >
              {label ?? t}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Footer ────────────────────────────────────────────────────── */
const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-richblack-700 bg-richblack-800">
      {/* ── Main grid ── */}
      <div className={`${DS.layout.container} py-14`}>
        <div className="grid grid-cols-2 gap-10 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr]">

          {/* Brand column — full width on mobile */}
          <div className="col-span-2 flex flex-col gap-5 lg:col-span-1">
            <Link to="/">
              <img
                src={Logo}
                alt="KodeVedic"
                className="h-10 w-auto object-contain"
                loading="lazy"
              />
            </Link>
            <p className="max-w-xs text-sm leading-relaxed text-richblack-400">
              KodeVedic is a digital learning platform by{" "}
              <a
                href="https://joinspaf.org"
                target="_blank"
                rel="noreferrer"
                className="text-yellow-50 hover:underline"
              >
                JoinSPAF
              </a>
              , empowering students across India with industry-relevant tech
              skills. Based in Gondia, Maharashtra.
            </p>

            {/* Social icons */}
            <div className="flex gap-3">
              {SOCIALS.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-richblack-700 text-richblack-400 transition-colors duration-150 hover:border-yellow-50/30 hover:bg-yellow-50/10 hover:text-yellow-50"
                >
                  <Icon className="text-base" />
                </a>
              ))}
            </div>
          </div>

          {/* Platform */}
          <LinkColumn title="Platform" links={PLATFORM_LINKS} />

          {/* Subjects & Languages from data file */}
          {FooterLink2.map((col) => (
            <LinkColumn key={col.title} title={col.title} links={col.links} />
          ))}
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="border-t border-richblack-700">
        <div
          className={`${DS.layout.container} flex flex-col items-center justify-between gap-4 py-5 sm:flex-row`}
        >
          {/* Legal links */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {LEGAL_LINKS.map(({ label, to }, i) => (
              <React.Fragment key={label}>
                {i > 0 && (
                  <span className="hidden h-3 w-px bg-richblack-600 sm:block" />
                )}
                <Link
                  to={to}
                  className="text-xs text-richblack-400 transition-colors duration-150 hover:text-richblack-5"
                >
                  {label}
                </Link>
              </React.Fragment>
            ))}
          </div>

          {/* Copyright */}
          <p className="text-xs text-richblack-500">
            © {year} KodeVedic · JoinSPAF · All Rights Reserved
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
